import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartAPI, wishlistAPI } from '../services/api';

const CartContext = createContext();

// Local storage key for guest cart
const GUEST_CART_KEY = 'r32_guest_cart';

export function CartProvider({ children }) {
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [guestCart, setGuestCart] = useState([]);

  // Load guest cart from localStorage
  const loadGuestCart = useCallback(() => {
    try {
      const saved = localStorage.getItem(GUEST_CART_KEY);
      if (saved) {
        const cart = JSON.parse(saved);
        setGuestCart(cart);
        const count = cart.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(count);
        return cart;
      }
    } catch (e) {
      console.error('Error loading guest cart:', e);
    }
    return [];
  }, []);

  // Save guest cart to localStorage
  const saveGuestCart = useCallback((items) => {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
      setGuestCart(items);
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(count);
    } catch (e) {
      console.error('Error saving guest cart:', e);
    }
  }, []);

  // Add item to guest cart
  const addToGuestCart = useCallback((product, quantity = 1) => {
    const currentCart = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
    
    const existingIndex = currentCart.findIndex(
      item => (item.productId === product._id || item.productId === product.id)
        && (item.stockId || null) === (product.stockId || null)
    );
    
    if (existingIndex >= 0) {
      currentCart[existingIndex].quantity += quantity;
    } else {
      currentCart.push({
        productId: product._id || product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        brand: product.brand,
        quantity: quantity,
        supplierId: product.supplierId,
        supplierName: product.supplierName,
        sourceId: product.sourceId,
        slug: product.slug,
        stockId: product.stockId || null,
        size: product.size || null,
        color: product.color || null,
        shippingOnRequest: !!product.shippingOnRequest
      });
    }
    
    saveGuestCart(currentCart);
    window.dispatchEvent(new Event('cartUpdated'));
    return currentCart;
  }, [saveGuestCart]);

  // Update guest cart item quantity
  const updateGuestCartItem = useCallback((productId, quantity, stockId = null) => {
    const currentCart = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
    const updatedCart = currentCart.map(item => 
      item.productId === productId && (item.stockId || null) === (stockId || null) ? { ...item, quantity } : item
    );
    saveGuestCart(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
    return updatedCart;
  }, [saveGuestCart]);

  // Remove item from guest cart
  const removeFromGuestCart = useCallback((productId, stockId = null) => {
    const currentCart = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
    const updatedCart = currentCart.filter(
      item => !(item.productId === productId && (item.stockId || null) === (stockId || null)));
    saveGuestCart(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
    return updatedCart;
  }, [saveGuestCart]);

  // Clear guest cart
  const clearGuestCart = useCallback(() => {
    localStorage.removeItem(GUEST_CART_KEY);
    setGuestCart([]);
    setCartCount(0);
    window.dispatchEvent(new Event('cartUpdated'));
  }, []);

  // Get guest cart items
  const getGuestCart = useCallback(() => {
    try {
      const saved = localStorage.getItem(GUEST_CART_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  }, []);

  useEffect(() => {
    loadCounts();
    
    // Listen for cart/wishlist updates from other components
    const handleCartUpdate = () => loadCounts();
    const handleWishlistUpdate = () => loadCounts();
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
  }, []);

  const loadCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // User not logged in - use guest cart
        const guestCartItems = loadGuestCart();
        const count = guestCartItems.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(count);
        setWishlistCount(0);
        setLoading(false);
        return;
      }

      // User is logged in - load from API
      try {
        const cartResponse = await cartAPI.get();
        const cartItems = cartResponse.data?.items || [];
        const newCartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
        console.log('[CartContext] Cart count:', newCartCount);
        setCartCount(newCartCount);
      } catch (e) {
        console.log('[CartContext] Cart error:', e);
        // Fallback to guest cart
        const guestCartItems = loadGuestCart();
        const count = guestCartItems.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(count);
      }

      // Load wishlist count
      try {
        const wishlistResponse = await wishlistAPI.get();
        const wishlistProducts = wishlistResponse.data?.products || wishlistResponse.data?.items || [];
        console.log('[CartContext] Wishlist products:', wishlistProducts.length);
        setWishlistCount(wishlistProducts.length);
      } catch (e) {
        console.log('[CartContext] Wishlist error:', e);
        setWishlistCount(0);
      }
    } catch (error) {
      console.error('Error loading counts:', error);
      setCartCount(0);
      setWishlistCount(0);
    } finally {
      setLoading(false);
    }
  };

  const refreshCounts = () => {
    loadCounts();
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!localStorage.getItem('token');
  };

  return (
    <CartContext.Provider value={{ 
      cartCount, 
      wishlistCount, 
      refreshCounts, 
      loading,
      // Guest cart functions
      guestCart,
      addToGuestCart,
      updateGuestCartItem,
      removeFromGuestCart,
      clearGuestCart,
      getGuestCart,
      isAuthenticated
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
